import { useAtomValue, useSetAtom } from "jotai";
import {
	type FC,
	type KeyboardEvent,
	type MouseEvent,
	useCallback,
	useContext,
} from "react";
import { audioEngine } from "$/modules/audio/audio-engine.ts";
import type { WordSegment } from "$/modules/segmentation/utils/segment-processing.ts";
import {
	SyncWordDisplayMode,
	syncWordDisplayModeAtom,
} from "$/modules/settings/states/index.ts";
import {
	selectedWordIdAtom,
	timelineDragAtom,
} from "$/modules/spectrogram/states/dnd.ts";
import type { WordVisualState } from "$/modules/spectrogram/utils/timeline-boundary.ts";
import { editingTimeFieldAtom } from "$/states/main.ts";
import styles from "./LyricWordSegment.module.css";
import { SpectrogramContext } from "./SpectrogramContext.ts";

interface LyricWordSegmentProps {
	lineId: string;
	segment: WordSegment;
	lineStartTime: number;
	visualState: WordVisualState;
	onPointerEnter: (id: string) => void;
	onPointerLeave: (id: string) => void;
	onFocus: (id: string) => void;
	onBlur: (id: string) => void;
}

export const LyricWordSegment: FC<LyricWordSegmentProps> = ({
	lineId,
	segment,
	lineStartTime,
	visualState,
	onPointerEnter,
	onPointerLeave,
	onFocus,
	onBlur,
}) => {
	const setSelectedWordId = useSetAtom(selectedWordIdAtom);
	const setTimelineDrag = useSetAtom(timelineDragAtom);
	const { zoom, scrollLeft, scrollContainerRef } =
		useContext(SpectrogramContext);
	const wordDisplayMode = useAtomValue(syncWordDisplayModeAtom);
	// 音译相关模式（只显示音译 / 音节+音译 / 三行）下在频谱段上显示逐字音译
	const showPerWordRomanization =
		wordDisplayMode === SyncWordDisplayMode.Roman ||
		wordDisplayMode === SyncWordDisplayMode.SyllableRoman ||
		wordDisplayMode === SyncWordDisplayMode.All;
	const editingTimeField = useAtomValue(editingTimeFieldAtom);

	const handlePointerEnter = useCallback(
		() => onPointerEnter(segment.id),
		[onPointerEnter, segment.id],
	);
	const handlePointerLeave = useCallback(
		() => onPointerLeave(segment.id),
		[onPointerLeave, segment.id],
	);
	const handleFocus = useCallback(
		() => onFocus(segment.id),
		[onFocus, segment.id],
	);
	const handleBlur = useCallback(
		() => onBlur(segment.id),
		[onBlur, segment.id],
	);

	const { startTime, endTime, word, romanWord } = segment;

	if (startTime == null || endTime == null || endTime <= startTime) {
		return null;
	}

	const left = ((startTime - lineStartTime) / 1000) * zoom;
	const width = ((endTime - startTime) / 1000) * zoom;

	const handleClick = (e: MouseEvent<HTMLDivElement>) => {
		if (editingTimeField) return;
		e.stopPropagation();
		setSelectedWordId(segment.id);
	};

	const handlePanStart = (e: MouseEvent<HTMLDivElement>) => {
		if (editingTimeField) return;
		if (e.button !== 0) return;

		e.preventDefault();
		e.stopPropagation();

		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) return;
		const rect = scrollContainer.getBoundingClientRect();

		const mouseXPx = e.clientX - rect.left;
		const initialMouseTimeMS = ((scrollLeft + mouseXPx) / zoom) * 1000;

		setTimelineDrag({
			type: "word-pan",
			lineId: lineId,
			wordId: segment.id,
			initialMouseTimeMS,
			initialWordStartMS: segment.startTime,
		});

		setSelectedWordId(segment.id);
	};

	const handleContextMenu = (e: MouseEvent<HTMLDivElement>) => {
		if (editingTimeField) return;
		e.preventDefault();
		e.stopPropagation();
		setSelectedWordId(segment.id);
		if (startTime != null && endTime != null) {
			audioEngine.auditionRange(startTime / 1000, endTime / 1000);
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			e.stopPropagation();
			setSelectedWordId(segment.id);
			if (startTime != null && endTime != null) {
				audioEngine.auditionRange(startTime / 1000, endTime / 1000);
			}
		}
	};

	const hasRoman =
		showPerWordRomanization && romanWord && romanWord.trim() !== "";

	return (
		<div
			className={styles.wordSegment}
			style={{ left: `${left}px`, width: `${width}px` }}
			data-visual-state={visualState}
			onClick={handleClick}
			onMouseDown={handlePanStart}
			onContextMenu={handleContextMenu}
			onPointerEnter={handlePointerEnter}
			onPointerLeave={handlePointerLeave}
			onFocus={handleFocus}
			onBlur={handleBlur}
			role="button"
			tabIndex={0}
			onKeyDown={handleKeyDown}
		>
			{hasRoman ? (
				<>
					<span className={styles.romanText}>{romanWord}</span>
					<span className={styles.originText}>{word}</span>
				</>
			) : (
				word
			)}
		</div>
	);
};
